import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setDefaultHarness } from '@/lib/api/generated/api';

import { toHarnessError } from '../errors';
import { harnessKeys } from '../queryKeys';
import type { Harness } from '../types';

/**
 * Mark one Harness as the organization's default (spec FR-009a).
 *
 * The backend flips the previous default `false` and the new one `true`
 * atomically in one transaction and answers with `204 No Content` — this
 * hook applies that same shape optimistically to the cached list so the
 * badge moves immediately, with `onError` rollback to the snapshot taken in
 * `onMutate`.
 */
export function useSetDefaultHarness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const response = await setDefaultHarness(id);
      if (response.status === 204) return;
      throw toHarnessError(response.status, response.data);
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries({ queryKey: harnessKeys.list() });
      const previous = queryClient.getQueryData<Harness[]>(harnessKeys.list());
      if (previous) {
        queryClient.setQueryData<Harness[]>(
          harnessKeys.list(),
          previous.map((harness) => ({
            ...harness,
            is_default: harness.id === id,
          })),
        );
      }
      return { previous };
    },
    onError: (_error, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(harnessKeys.list(), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: harnessKeys.all() });
    },
  });
}
